import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TopicCard from "@/components/feature/TopicCard";
import usePageMeta from "@/hooks/usePageMeta";
import { categories } from "@/mocks/guaData";
import { getCategoryBySlug, getTopicsByCategory } from "@/utils/content";

const SITE_URL = "https://hshplx.cn";

type SortKey = "heat" | "latest";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const [sortKey, setSortKey] = useState<SortKey>("heat");
  const category = getCategoryBySlug(slug);

  const list = useMemo(() => {
    const base = getTopicsByCategory(slug);
    if (sortKey === "latest") {
      return [...base].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
    }
    return [...base].sort((a, b) => b.heat - a.heat);
  }, [slug, sortKey]);

  usePageMeta({
    title: category
      ? `${category.name} - 热门吃瓜话题分类 | 瓜田热榜`
      : "分类不存在 - 瓜田热榜",
    description: category
      ? `${category.name}分类聚合页：${category.description}实时更新该分类下的热门吃瓜话题与讨论焦点。`
      : "该分类页面不存在，返回首页浏览今日热门吃瓜榜。",
    keywords: category
      ? `${category.name},热门吃瓜,${category.name}话题,今日热门吃瓜榜`
      : "热门吃瓜,今日热门吃瓜榜",
    canonical: `${SITE_URL}/category/${slug}`,
    schema: category
      ? [
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${category.name} - 热门吃瓜话题分类`,
            url: `${SITE_URL}/category/${slug}`,
            inLanguage: "zh-CN",
            description: category.description,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: category.name, item: `${SITE_URL}/category/${slug}` },
            ],
          },
        ]
      : undefined,
  });

  if (!category) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-20">
          <div className="max-w-md text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-compass-3-line text-2xl leading-none" aria-hidden="true" />
            </span>
            <h1 className="heading-font mt-5 text-2xl font-black text-foreground-950">没有找到这个分类</h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              可能是链接已经失效，也可能这个分类还没上线。回到首页看看今天的榜单吧。
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/"
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                返回首页
              </Link>
              <Link
                to="/rank"
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                查看完整榜单
              </Link>
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <Breadcrumb items={[{ label: "分类速览", to: "/#categories" }, { label: category.name }]} />

      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100/60">
          <div className="mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-2xl">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">
                  <span className="flex h-5 w-5 items-center justify-center">
                    <i className={`${category.icon} text-base leading-none`} aria-hidden="true" />
                  </span>
                  主题分类
                </p>
                <h1 className="heading-font mt-3 text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                  {category.name}
                </h1>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                  {category.description}
                </p>
              </div>

              <div className="flex items-center gap-1 self-start rounded-full bg-background-200/70 px-1 py-1">
                <button
                  type="button"
                  onClick={() => setSortKey("heat")}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    sortKey === "heat"
                      ? "bg-background-50 text-foreground-950"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  按热度
                </button>
                <button
                  type="button"
                  onClick={() => setSortKey("latest")}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    sortKey === "latest"
                      ? "bg-background-50 text-foreground-950"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  按时间
                </button>
              </div>
            </div>

            <nav aria-label="其他分类" className="mt-8 flex flex-wrap items-center gap-2">
              {categories.map((item) => (
                <Link
                  key={item.slug}
                  to={`/category/${item.slug}`}
                  className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    item.slug === slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-200/70 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  <i className={`${item.icon} text-sm leading-none`} aria-hidden="true" />
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="heading-font text-lg font-bold text-foreground-950 md:text-xl">
                该分类下的热议话题
              </h2>
              <span className="text-sm text-foreground-500">共 {list.length} 个话题</span>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
              {list.map((topic) => (
                <TopicCard key={topic.id} topic={topic} />
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 gap-4 rounded-lg border border-background-200 bg-background-100/60 p-5 md:grid-cols-3 md:p-6">
              <div className="md:col-span-2">
                <h3 className="heading-font text-base font-bold text-foreground-950">
                  想看看别的圈子在聊什么？
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  分类只是入口，热点常常跨圈传播。回到榜单页能一次看到所有分类的实时排序。
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 md:justify-end">
                <Link
                  to="/rank"
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看今日热榜
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-right-line text-base leading-none" aria-hidden="true" />
                  </span>
                </Link>
                <Link
                  to="/"
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  返回首页
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}